import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Alert, Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel, LinearProgress, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
const BATCH_SIZES = [1, 3, 5, 10, 25, 50, 100];
const STATUS_LABELS = {
  ready: 'Ready', review: 'Needs Review', blocked: 'Blocked', unchanged: 'Already Net 30',
  skipped: 'Skipped', updated: 'Updated', failed: 'Failed',
};
const keyFor = row => String(row.rowNumber);
const isChange = row => row.status === 'ready' || row.status === 'review';
const asSelection = row => ({
  rowNumber: row.rowNumber, sourceName: row.sourceName, sourceTerms: row.sourceTerms,
  customerId: row.matchedCustomer.id, expectedPreviousTerms: row.matchedCustomer.previousTerms ?? '',
  expectedEffectiveTermId: row.matchedCustomer.effectiveTermId ?? '',
  confirmOverwrite: Boolean(row.matchedCustomer.previousTerms),
  manualMatchConfirmed: row.manualMatchConfirmed === true,
});
const downloadCsv = (rows) => {
  const columns = ['Row', 'Spreadsheet Customer', 'Matched Customer', 'Customer ID', 'Stored Customer Terms', 'Current Effective Terms', 'Effective Source', 'Target Terms', 'Result', 'Reason'];
  const escape = value => '"' + String(value == null ? '' : value).replaceAll('"', '""') + '"';
  const csv = [columns, ...rows.map(row => [row.rowNumber, row.sourceName, row.matchedCustomer?.businessName, row.matchedCustomer?.id, row.matchedCustomer?.currentTerms, row.matchedCustomer?.effectiveTerms, row.matchedCustomer?.effectiveSource, row.sourceTerms, row.resultStatus || row.status, row.resultReason || row.reason])].map(line => line.map(escape).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a'); anchor.href = url; anchor.download = 'customer-terms-repair-results.csv'; anchor.click(); URL.revokeObjectURL(url);
};
export default function CustomerTermsRepair({ instanceUniqueId }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [approved, setApproved] = useState({});
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [batchSize, setBatchSize] = useState(100);
  const [testResult, setTestResult] = useState(null);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [searchOptions, setSearchOptions] = useState({});
  const headers = { accesstoken: localStorage.getItem('accessToken') };
  const base = SERVER + '/api/instances/' + instanceUniqueId + '/tools/import/customer-terms';
  const counts = useMemo(() => rows.reduce((acc, row) => { acc[row.status] = (acc[row.status] || 0) + 1; return acc; }, {}), [rows]);
  const visible = useMemo(() => rows.filter(row => filter === 'all' || row.status === filter), [rows, filter]);
  const readyRows = rows.filter(row => approved[keyFor(row)] && isChange(row) && row.matchedCustomer && !row.matchedCustomer.creditHold);
  const nextBatch = readyRows.slice(0, batchSize);
  const batchSelections = nextBatch.map(asSelection);
  const batchFingerprint = JSON.stringify(batchSelections);
  const currentTest = testResult?.fingerprint === batchFingerprint ? testResult.data : null;
  const testPassed = Boolean(currentTest && currentTest.results.length === nextBatch.length
    && currentTest.results.every(row => ['would_update', 'unchanged'].includes(row.status)));
  const previewFile = async () => {
    if (!file) return;
    setBusy(true); setError(''); setResult(null); setTestResult(null); setNeedsRefresh(false);
    try {
      const data = new FormData(); data.append('file', file);
      const response = await axios.post(base + '/preview', data, { headers });
      setPreview(response.data); setRows(response.data.rows);
      setApproved({}); // Explicitly select test accounts; nothing is pre-approved on upload.
    } catch (e) { setError(e.response?.data?.message || 'Unable to review spreadsheet.'); }
    finally { setBusy(false); }
  };
  const selectCandidate = (row, candidate) => {
    const next = rows.map(existing => {
      if (existing.rowNumber !== row.rowNumber) return existing;
      const status = !candidate ? 'review' : candidate.creditHold ? 'blocked' : Number(candidate.currentTermId) === Number(preview.target.id) ? 'unchanged' : 'review';
      return { ...existing, matchedCustomer: candidate || null, status, matchType: 'manual', manualMatchConfirmed: Boolean(candidate),
        reason: !candidate ? 'Choose an existing customer.' : candidate.creditHold ? 'Credit Hold cannot be changed here.' : status === 'unchanged' ? 'Already Net 30.' : 'Manual match: explicitly approve before updating.' };
    });
    setRows(next); setApproved(current => ({ ...current, [keyFor(row)]: false }));
    setTestResult(null);
  };
  const search = async (row, query) => {
    if (query.trim().length < 2) return;
    try {
      const response = await axios.get(base + '/candidates', { params: { q: query }, headers });
      setSearchOptions(current => ({ ...current, [keyFor(row)]: response.data.customers || [] }));
    } catch { /* existing suggestions remain available */ }
  };
  const testImport = async () => {
    if (!nextBatch.length || busy || needsRefresh) return;
    setBusy(true); setError(''); setTestResult(null);
    try {
      const response = await axios.post(base + '/test-import', { selections: batchSelections }, { headers });
      if (!Array.isArray(response.data?.results) || response.data.results.length !== batchSelections.length) {
        throw new Error('Test Import returned an incomplete result.');
      }
      setTestResult({ fingerprint: batchFingerprint, data: response.data });
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Test Import could not complete.');
    } finally { setBusy(false); }
  };
  const applyTestedBatch = async () => {
    if (!testPassed || busy || needsRefresh || !nextBatch.length) return;
    const names = nextBatch.map(row => '• ' + row.matchedCustomer.businessName + ' (#' + row.matchedCustomer.id + ')').join('\n');
    const confirmed = window.confirm(
      'Apply Net 30 to ONLY these ' + nextBatch.length + ' approved customers?\n\n'
      + names + '\n\nThis WILL save their new customer terms. Other spreadsheet rows will not be changed.'
    );
    if (!confirmed) return;
    setBusy(true); setError('');
    try {
      // One deliberately small batch only. Never loop through the full sheet automatically.
      const response = await axios.post(base + '/commit', { selections: batchSelections }, { headers });
      if (!Array.isArray(response.data?.results) || response.data.results.length !== batchSelections.length) {
        throw new Error('The apply result was incomplete.');
      }
      const completed = response.data.results;
      const byRow = new Map(completed.map(row => [row.rowNumber, row]));
      setRows(current => current.map(row => {
        const outcome = byRow.get(row.rowNumber);
        return outcome
          ? { ...row, status: outcome.status, resultStatus: outcome.status,
            resultReason: outcome.reason, reason: outcome.reason }
          : row;
      }));
      setApproved(current => {
        const remaining = { ...current };
        nextBatch.forEach(row => { delete remaining[keyFor(row)]; });
        return remaining;
      });
      setTestResult(null); // Every subsequent batch must be tested independently.
      setResult(current => ({
        updated: (current?.updated || 0) + completed.filter(row => row.status === 'updated').length,
        unchanged: (current?.unchanged || 0) + completed.filter(row => row.status === 'unchanged').length,
        blocked: (current?.blocked || 0) + completed.filter(row => row.status === 'blocked').length,
        failed: (current?.failed || 0) + completed.filter(row => row.status === 'failed').length,
      }));
    } catch (e) {
      // A response can fail after the server has written records: never blindly retry that batch.
      setError((e.response?.data?.message || e.message || 'Batch result unavailable.')
        + ' Refresh Preview to confirm which accounts changed before doing another batch.');
      setNeedsRefresh(true);
      setTestResult(null);
      setApproved({});
    } finally { setBusy(false); }
  };
  return <Box sx={{ display: 'grid', gap: 2 }}>
    <Typography variant="h6">Customer Terms Update</Typography>
    <Typography variant="body2" color="text.secondary">Update existing customer payment terms from a spreadsheet. No customers, contacts, projects, invoices or orders are created or regenerated.</Typography>
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      <Button component="label" variant="outlined">Choose Excel / CSV<input hidden type="file" accept=".xlsx,.xls,.csv" onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); setRows([]); setResult(null); setApproved({}); setTestResult(null); setNeedsRefresh(false); setFilter('all'); setPage(0); }} /></Button>
      <Typography variant="body2">{file?.name || 'Select your Net 30 Accounts sheet'}</Typography>
      <Button variant="contained" disabled={!file || busy} onClick={previewFile}>Analyze Customers</Button>
      {busy && <LinearProgress sx={{ width: 120 }} />}
    </Paper>
    {error && <Alert severity="error">{error}</Alert>}
    {preview && <>
      <Alert severity="info">
        Target: <strong>{preview.target.name} (Terms ID #{preview.target.id})</strong>.
        “Not assigned” means no customer-specific term was saved.
        {preview.defaultTerms
          ? ` The configured instance default is ${preview.defaultTerms.name} (Terms ID #${preview.defaultTerms.id}), shown separately below.`
          : ' There is no configured instance-level default; an older customer edit form may have shown an unsaved first dropdown option.'}
        Only explicitly approved customer accounts are updated.
      </Alert>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {['all', 'ready', 'review', 'blocked', 'unchanged', 'skipped', 'updated', 'failed'].map(status =>
          <Chip key={status} clickable variant={filter === status ? 'filled' : 'outlined'} color={status === 'blocked' || status === 'failed' ? 'error' : status === 'ready' || status === 'updated' ? 'success' : 'default'} label={(status === 'all' ? 'All' : STATUS_LABELS[status] || status) + ' · ' + (status === 'all' ? rows.length : counts[status] || 0)} onClick={() => { setFilter(status); setPage(0); }} />
        )}
      </Box>
      <Paper variant="outlined" sx={{ maxHeight: 450, overflow: 'auto' }}>
        <Table stickyHeader size="small"><TableHead><TableRow>
          {['Approve', 'Row', 'Spreadsheet Name', 'Matched Existing Customer', 'Saved Customer Terms', 'Effective Terms Now', 'New Terms', 'Status / Explanation'].map(label => <TableCell key={label} sx={{ fontWeight: 700 }}>{label}</TableCell>)}
        </TableRow></TableHead><TableBody>{visible.slice(page * 100, (page + 1) * 100).map(row => <TableRow key={row.rowNumber}>
          <TableCell><Checkbox size="small" checked={Boolean(approved[keyFor(row)])} disabled={busy || !isChange(row) || !row.matchedCustomer || row.matchedCustomer.creditHold} onChange={e => setApproved(current => ({ ...current, [keyFor(row)]: e.target.checked }))}/></TableCell>
          <TableCell>{row.rowNumber}</TableCell>
          <TableCell>{row.sourceName}</TableCell>
          <TableCell sx={{ minWidth: 250 }}>
            {row.status === 'review' || (row.status === 'blocked' && row.candidates?.length) ? <>
              <Autocomplete
                size="small"
                options={[...(row.candidates || []), ...(searchOptions[keyFor(row)] || [])]
                  .filter((candidate, index, list) => list.findIndex(other => other.id === candidate.id) === index)}
                value={row.matchedCustomer || null}
                getOptionLabel={option => option.businessName + ' (#' + option.id + ')'}
                isOptionEqualToValue={(left, right) => left.id === right.id}
                onChange={(_, candidate) => selectCandidate(row, candidate)}
                onInputChange={(_, value, reason) => { if (reason === 'input') search(row, value); }}
                renderOption={(props, option) => <li {...props} key={option.id}>
                  <Box sx={{ display: 'grid' }}>
                    <Typography variant="body2">{option.businessName} (#{option.id})</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Saved: {option.currentTerms || 'Not assigned'} · Effective: {option.effectiveTerms || 'No configured default'}
                    </Typography>
                  </Box>
                </li>}
                renderInput={params => <TextField {...params} placeholder="Find existing customer"
                  helperText={row.candidates?.length
                    ? row.candidates.length + ' suggested existing account(s) — confirm identity'
                    : 'Search for the customer manually'} />}
              />
              {!!row.candidates?.length && !row.matchedCustomer && <Box sx={{ display: 'grid', gap: 0.3, mt: 0.5 }}>
                {row.candidates.slice(0, 3).map(candidate => <Button
                  key={candidate.id}
                  size="small"
                  variant="text"
                  disabled={busy}
                  onClick={() => selectCandidate(row, candidate)}
                  sx={{ justifyContent: 'start', textAlign: 'left', textTransform: 'none', whiteSpace: 'normal', fontSize: 12 }}
                >
                  Suggested: {candidate.businessName} (#{candidate.id}) · {candidate.currentTerms} · Effective: {candidate.effectiveTerms}
                </Button>)}
              </Box>}
            </> : row.matchedCustomer ? row.matchedCustomer.businessName + ' (#' + row.matchedCustomer.id + ')' : '—'}
          </TableCell>
          <TableCell>{row.matchedCustomer?.currentTerms || '—'}</TableCell>
          <TableCell>
            {row.matchedCustomer?.effectiveTerms || '—'}
            {row.matchedCustomer?.effectiveSource === 'instance_default'
              && <Typography display="block" variant="caption" color="text.secondary">Inherited instance default</Typography>}
          </TableCell>
          <TableCell>{row.sourceTerms}</TableCell>
          <TableCell><Chip size="small" label={STATUS_LABELS[row.status] || row.status} color={['blocked', 'failed'].includes(row.status) ? 'error' : ['ready','updated'].includes(row.status) ? 'success' : 'default'} /><Typography variant="caption" display="block">{row.reason}</Typography></TableCell>
        </TableRow>)}</TableBody></Table>
      </Paper>
      {visible.length > 100 && <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}><Button size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button><Typography variant="caption">Page {page + 1} of {Math.ceil(visible.length / 100)} · {visible.length} rows</Typography><Button size="small" disabled={(page + 1) * 100 >= visible.length} onClick={() => setPage(p => p + 1)}>Next</Button></Box>}
      {result && <Alert severity={result.blocked || result.failed ? 'warning' : 'success'}>
        Applied batches so far: Updated {result.updated} · Already correct {result.unchanged} · Blocked {result.blocked} · Failed {result.failed}
      </Alert>}
      {needsRefresh && <Alert severity="warning">An apply response was interrupted. Refresh Preview before running another test or batch.</Alert>}
      <FormControlLabel control={<Checkbox checked={rows.some(row => row.status === 'ready') && rows.filter(row => row.status === 'ready').every(row => approved[keyFor(row)])} onChange={e => { setApproved(current => ({ ...current, ...Object.fromEntries(rows.filter(row => row.status === 'ready').map(row => [keyFor(row), e.target.checked])) })); setTestResult(null); }} />} label="Approve all exact matches with missing terms" />
      <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>Test Import · Small Batches</Typography>
        <Typography variant="body2" color="text.secondary">
          {readyRows.length} approved changes remaining. Select the customers you want to try (or approve all exact matches),
          test the next small batch without writing anything, then explicitly apply only those tested accounts. Repeat as needed.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2">Accounts per batch</Typography>
          <Select size="small" value={batchSize} onChange={event => { setBatchSize(Number(event.target.value)); setTestResult(null); }}
            inputProps={{ 'aria-label': 'Accounts per test batch' }}>
            {BATCH_SIZES.map(size => <MenuItem key={size} value={size}>{size}</MenuItem>)}
          </Select>
          <Button variant="outlined" disabled={busy || needsRefresh || !nextBatch.length} onClick={testImport}>
            Test Import ({nextBatch.length}) · No Changes
          </Button>
          <Button variant="contained" disabled={busy || needsRefresh || !testPassed} onClick={applyTestedBatch}>
            Apply Tested Batch ({nextBatch.length})
          </Button>
          <Button variant="outlined" disabled={busy} onClick={() => downloadCsv(rows)}>Download Results CSV</Button>
        </Box>
        {!nextBatch.length && <Typography variant="caption" color="text.secondary">No customers approved yet — check a few rows above to start a test import.</Typography>}
        {nextBatch.length > 0 && <Typography variant="caption" color="text.secondary">
          Next batch: {nextBatch.map(row => `${row.matchedCustomer.businessName} (#${row.matchedCustomer.id})`).join(' · ')}
        </Typography>}
        {currentTest && <Alert severity={testPassed ? 'success' : 'warning'}>
          Test Import only — no customer records changed. Would update: {currentTest.summary.would_update || 0} ·
          Already correct: {currentTest.summary.unchanged || 0} · Blocked: {currentTest.summary.blocked || 0} ·
          Failed: {currentTest.summary.failed || 0}.
          {testPassed ? ' You can now apply exactly this tested batch.' : ' Resolve blocked rows and test again before applying.'}
        </Alert>}
        {currentTest && <Box sx={{ display: 'grid', gap: 0.5 }}>
          {currentTest.results.map(row => <Typography key={row.rowNumber} variant="caption">
            {row.sourceName} · Saved: {row.before || 'Not assigned'}
            {row.effectiveBefore ? ` · Effective now: ${row.effectiveBefore}${row.effectiveSource === 'instance_default' ? ' (inherited)' : ''}` : ''}
            {' → '} {row.after || 'Net 30'} · {row.status}: {row.reason}
          </Typography>)}
        </Box>}
      </Paper>
    </>}
  </Box>;
}
